import { useState, useEffect } from "react";
import { Activity as ActivityIcon, User, FileText, Trash2, Edit, KeyRound, Shield, Database, CheckSquare, RefreshCw } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import api from "../services/api";

const ACTION_META = {
  created_lead:        { icon: FileText,    color: "bg-green-100 text-green-800 border-green-200" },
  updated_lead:        { icon: Edit,        color: "bg-blue-100 text-blue-800 border-blue-200" },
  deleted_lead:        { icon: Trash2,      color: "bg-red-100 text-red-800 border-red-200" },
  created_user:        { icon: User,        color: "bg-cyan-100 text-cyan-800 border-cyan-200" },
  deleted_user:        { icon: Trash2,      color: "bg-orange-100 text-orange-800 border-orange-200" },
  activated_user:      { icon: User,        color: "bg-emerald-100 text-emerald-800 border-emerald-200" },
  deactivated_user:    { icon: User,        color: "bg-gray-100 text-gray-600 border-gray-200" },
  changed_role:        { icon: Shield,      color: "bg-purple-100 text-purple-800 border-purple-200" },
  changed_password:    { icon: KeyRound,    color: "bg-indigo-100 text-indigo-800 border-indigo-200" },
  cleared_data:        { icon: Database,    color: "bg-red-100 text-red-800 border-red-200" },
  created_sequence:    { icon: RefreshCw,   color: "bg-teal-100 text-teal-800 border-teal-200" },
  assigned_sequence:   { icon: CheckSquare, color: "bg-teal-100 text-teal-800 border-teal-200" },
  created_outreach:    { icon: FileText,    color: "bg-violet-100 text-violet-800 border-violet-200" },
  updated_outreach:    { icon: Edit,        color: "bg-violet-100 text-violet-800 border-violet-200" },
};

const relTime = (ts) => {
  const diff = Math.floor((Date.now() - new Date(ts)) / 1000);
  if (diff < 60)     return "just now";
  if (diff < 3600)   return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400)  return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
  return new Date(ts).toLocaleDateString();
};

export default function ActivityLogs() {
  const [logs, setLogs]       = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/api/activity-logs?limit=200')
      .then(r => setLogs(r.data))
      .catch(() => toast.error("Failed to fetch activity logs"))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-lg font-semibold text-zinc-900">Activity Logs</h1>
        <p className="text-xs text-zinc-500 mt-0.5">All team actions, newest first</p>
      </div>

      {loading ? (
        <div className="space-y-3">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="bg-white border border-zinc-200 rounded-lg p-4 animate-pulse">
              <div className="flex gap-3">
                <div className="w-9 h-9 bg-zinc-100 rounded-full shrink-0" />
                <div className="flex-1 space-y-2">
                  <div className="h-3 bg-zinc-100 rounded w-48" />
                  <div className="h-3 bg-zinc-100 rounded w-72" />
                  <div className="h-2.5 bg-zinc-100 rounded w-24" />
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="space-y-2">
          {logs.map((log, i) => {
            const meta = ACTION_META[log.action] || { icon: ActivityIcon, color: "bg-zinc-100 text-zinc-700 border-zinc-200" };
            const Icon = meta.icon;
            return (
              <div key={log.id || i} className="bg-white border border-zinc-200 rounded-lg p-4 hover:border-zinc-300 transition-colors">
                <div className="flex gap-3">
                  <div className="w-9 h-9 bg-zinc-100 rounded-full flex items-center justify-center shrink-0">
                    <Icon size={15} className="text-zinc-500" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-0.5">
                      <span className="text-sm font-medium text-zinc-800">{log.user_name || log.user_email}</span>
                      <Badge className={`${meta.color} border text-xs`}>
                        {log.action.replace(/_/g, " ")}
                      </Badge>
                    </div>
                    <p className="text-sm text-zinc-600 mb-1">{log.details}</p>
                    <div className="flex items-center gap-2 text-xs text-zinc-400">
                      <span className="capitalize">{log.entity_type}</span>
                      <span>·</span>
                      <span>{relTime(log.timestamp)}</span>
                    </div>
                  </div>
                  <span className="text-xs text-zinc-400 shrink-0">
                    {new Date(log.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  </span>
                </div>
              </div>
            );
          })}
          {logs.length === 0 && (
            <div className="text-center py-16">
              <ActivityIcon size={32} className="text-zinc-200 mx-auto mb-2" />
              <p className="text-sm text-zinc-400">No activity yet.</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
